import { pcbLayoutRootNodeParentId } from "@buildwithflux/models";
import { topologicalSort } from "./functions";
/**
 * Adds new childUids to a node
 *
 * This function will initialize node.childUids to empty array if its
 * not set yet
 *
 * @param node - the node we are adding `childUids` to
 * @param childUids - new childUids we want to add
 */
export const addChildUidsToPcbNode = (node, childUids) => {
    if (!node.childUids) {
        // Question: this is surprising. When are the childUids not present?
        // Typing indicates the array is always present. Initialization of this array seems like an odd/unexpected
        // side-effect of a function intended to add childUids to a node. What if childUids is an empty array, and
        // node.childUids is uninitialized? Would initializing it to an empty array still be considered correct behavior?
        node.childUids = [];
    }
    for (const childUid of childUids) {
        if (!node.childUids.includes(childUid)) {
            node.childUids.push(childUid);
        }
    }
};
/**
 * This function only adds the passed in `newNodes` to map, and
 * will NOT update parentNode's childUids field.
 *
 * @param pcbNodes - The nodes map we are adding nodes to
 * @param newNodes - Array of nodes we are adding
 */
export const addPcbNodesToMap = (pcbNodes, newNodes) => {
    // First, add every new node to the map
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    newNodes.forEach((node) => {
        pcbNodes[node.uid] = node;
    });
};
/**
 * This function not only adds the `newNodes` to map, but also tries
 * to find parent of each new node and update its childUids.
 *
 * It will log an error if a parent node is not found!
 *
 * @param pcbNodes - The nodes map we are adding nodes to
 * @param newNodes - Array of nodes we are adding
 * @param {boolean} skipSort - skip topological sort if set. We only skip when reloading the whole board
 * @param parentNotFoundStrategy - Whether to "throw" an error or "log" a warning if parent node is not found. By default this is "throw"
 */
export const addPcbNodes = (pcbNodes, newNodes, skipSort, parentNotFoundStrategy = "throw") => {
    // NOTE: Sorting new nodes topologically first, this is a work around to make sure
    // we dont add any child node before its parent node.
    // TODO: Later we should remove this work around, and make let the document domain (i.e. this
    // method) handle the work around case. It should:
    // 1. always add the parent node first if its not found in `pcbNodes` yet
    // 2. report errors if parent node is not found in `pcbNodes` nor in `newNodes`
    // When remove this work around, search for `skipSort` and remove them too
    const nodes = skipSort ? newNodes : topologicalSort(newNodes);
    // First, add every new node to the map
    addPcbNodesToMap(pcbNodes, nodes);
    // Then, update parent-child relationship. At this point every
    // parentNode should be in map already. If not, thats an error
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    nodes.forEach((node) => {
        // Skip updating parent node if it is a `ROOT` node
        if (node.parentUid === pcbLayoutRootNodeParentId)
            return;
        const parentNode = pcbNodes[node.parentUid];
        if (!node.childUids) {
            // Ensure all added nodes have a valid childUids field, even if it has no children
            // (the addChildUidsToPcbNode call below only adds .childUids if there are some,
            // but code elsewhere, eg buildMeshTree, wants to be able assume the array exists
            // even if empty).
            node.childUids = [];
        }
        if (parentNode) {
            addChildUidsToPcbNode(parentNode, [node.uid]);
        }
        else if (parentNotFoundStrategy === "throw") {
            throw new Error(`Failed addChildUidsToPcbNode: parent does not exist. Node: ${node.name} ${node.type} ${node.uid}, parentUid: ${node.parentUid}`);
        }
        else {
            /* eslint-disable no-console */
            // Log warning instead of throwing - this can happen during race conditions
            // when the parent node was removed before the child could be added
            console.warn(`Failed addChildUidsToPcbNode: parent does not exist. Node: ${node.name} ${node.type} ${node.uid}, parentUid: ${node.parentUid}`);
        }
    });
};
//# sourceMappingURL=addNodes.js.map